import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Puzzle, Code } from 'lucide-react';
import { detect, BrowserInfo, Browser } from 'detect-browser';

import { getLangKey } from 'models/Language';
import {
  browserConfig,
  BrowserConfigType
} from 'components/onboarding/extensionConfig';
import ExtensionInstallButton from 'components/onboarding/install_button';
import { JavascriptContent } from 'appJS/views/new_project_settings/InstallBugherd/JavascriptContent';

import Accordion, { AccordionItem } from '../Accordion';
import * as translations from '../strings';
import { STEP_PATHS, WizardStep } from '../types';
import styles from '../index.module.css';

type Props = {
  projectId: number;
  apiKey: string;
  bugherdUrl: string;
};

const EXTENSION = 'extension';
const JAVASCRIPT = 'javascript';

const EXTENSION_STORE_NAMES: Record<string, string> = {
  chrome: 'Chrome Web Store',
  firefox: 'Firefox Extensions Store',
  safari: 'App Store',
  'edge-chromium': 'Edge Extensions Store'
};

const InstallBugHerd: React.FC<Props> = ({ projectId, apiKey, bugherdUrl }) => {
  const strings = translations[getLangKey()];
  const navigate = useNavigate();

  const browser = detect() as BrowserInfo;
  const currentBrowser: Browser | null = browser ? browser.name : null;
  const browserExtensionConfig: BrowserConfigType | null =
    (browserConfig.find(
      info => info.name === currentBrowser
    ) as BrowserConfigType) || null;
  const hasBrowserExtension = !!browserExtensionConfig;
  const extensionStoreName =
    (currentBrowser && EXTENSION_STORE_NAMES[currentBrowser]) ||
    'Chrome Web Store';

  // Extension is the recommended path, so it opens by default when the user
  // first lands on step 2. Browsers without an extension fall back to JS.
  const [openId, setOpenId] = useState<string>(
    hasBrowserExtension ? EXTENSION : JAVASCRIPT
  );

  const [
    javascriptInstalledOverride,
    setJavascriptInstalledOverride
  ] = useState<boolean>(false);

  const goToTryStep = () => navigate(STEP_PATHS[WizardStep.TRY]);

  const extensionItem: AccordionItem = {
    id: EXTENSION,
    icon: <Puzzle size={20} />,
    title: strings.installExtensionTitle(
      browserExtensionConfig ? browserExtensionConfig.display : 'Browser'
    ),
    description: strings.installExtensionDescription,
    body: (
      <>
        <p className={styles.accordionBodyText}>
          {strings.installExtensionDisclaimerText(extensionStoreName)}
        </p>
        <div className={styles.extensionButtonWrap}>
          <ExtensionInstallButton
            browserConfig={browserExtensionConfig}
            isSignUpOnBoarding
          />
        </div>
      </>
    )
  };

  const javascriptItem: AccordionItem = {
    id: JAVASCRIPT,
    icon: <Code size={20} />,
    title: strings.installJsTitle,
    description: strings.installJsDescription,
    body: (
      <>
        <p className={styles.accordionBodyText}>
          {strings.installJsDisclaimerText}
        </p>
        <JavascriptContent
          onInstallCallback={() => {
            setJavascriptInstalledOverride(true);
            goToTryStep();
          }}
          project={{
            apiKey,
            id: String(projectId),
            javascriptInstalledOverride
          }}
          bugherdUrl={bugherdUrl}
          isOnboarding
          goToNextStep={goToTryStep}
        />
      </>
    )
  };

  const items = hasBrowserExtension
    ? [extensionItem, javascriptItem]
    : [javascriptItem, extensionItem];

  return (
    <>
      <div className={styles.stepPill}>STEP 2</div>
      <h2 className={styles.stepHeading}>{strings.installHeading}</h2>
      <p className={styles.installBody}>
        After getting the browser extension or installing the Javascript code,
        you&apos;ll be able to click anything on your site and drop a feedback
        pin – <em>no more emailing screenshots.</em>
      </p>

      <Accordion items={items} openId={openId} onOpen={setOpenId} />
    </>
  );
};

export default InstallBugHerd;
